import { useEffect, useState, useRef, useCallback } from 'react';

interface AntdThemeToken {
  colorPrimary: string;
  colorSuccess: string;
  borderRadius: number;
  fontSize: number;
  fontFamily: string;
}

interface ThemeConfig {
  token: AntdThemeToken;
}

/**
 * Parses CSS custom properties from a computed style object and returns an Ant Design theme token object.
 * @param style The CSSStyleDeclaration object to parse.
 * @returns An AntdThemeToken object.
 */
function parseThemeFromCssVariables(
  style: CSSStyleDeclaration,
): AntdThemeToken {
  const primaryColor = style.getPropertyValue('--theme-primary-color').trim();
  const successColor = style.getPropertyValue('--theme-success-color').trim();
  const borderRadius = parseInt(
    style.getPropertyValue('--theme-border-radius').trim(),
  );
  const fontSize = parseInt(style.getPropertyValue('--theme-font-size').trim());
  const fontFamily = style.getPropertyValue('--theme-font-family').trim();
  return {
    colorPrimary: primaryColor,
    colorSuccess: successColor,
    borderRadius: borderRadius,
    fontSize: fontSize,
    fontFamily: fontFamily,
  };
}

/**
 * A React hook to dynamically load custom CSS from a URL and apply it as an Ant Design theme.
 * It observes the DOM for CSS variable availability to ensure the theme is applied correctly.
 *
 * @returns An object containing a boolean indicating whether the CSS is currently loading, and a function to load the custom styles.
 */
export function useCustomStyles() {
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedStyles = useRef(false);

  const loadCustomStyles = useCallback(
    (cssUrl: string | undefined, callback: (theme: ThemeConfig) => void) => {
      if (!cssUrl || hasLoadedStyles.current) {
        return;
      }

      setIsLoading(true);
      hasLoadedStyles.current = true; // Mark as loaded to prevent re-calling

      const loadAndApplyCSS = async () => {
        try {
          const response = await fetch(cssUrl);
          const cssText = await response.text();

          // Add new CSS
          const styleElement = document.createElement('style');
          styleElement.textContent = cssText;
          document.head.appendChild(styleElement);

          const applyThemeIfReady = () => {
            const isStyleInDOM = document.head.contains(styleElement);
            const style = getComputedStyle(document.documentElement);

            if (isStyleInDOM) {
              console.debug('CSS loaded and variables available:');
              callback({
                token: parseThemeFromCssVariables(style),
              });
              setIsLoading(false);
              return true;
            }
            return false;
          };

          // Attempt to apply theme immediately
          if (applyThemeIfReady()) {
            return;
          }

          // If not immediately ready, observe for changes
          const observer = new MutationObserver(() => {
            if (applyThemeIfReady()) {
              observer.disconnect();
            }
          });

          observer.observe(document.head, {
            childList: true,
            subtree: true,
            attributes: true,
          });

          observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style'],
          });
        } catch (error) {
          console.error('CSS load error:', error);
          setIsLoading(false);
          hasLoadedStyles.current = false; // Reset if loading fails
        }
      };

      loadAndApplyCSS();
    },
    [setIsLoading], // Dependencies for useCallback
  );

  useEffect(() => {
    return () => {
      hasLoadedStyles.current = false;
    };
  }, []);

  return { isLoading, loadCustomStyles };
}
